<script setup lang="ts">
import { ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { displayName, signOut, subject, usingDevAuth } from '@/lib/session';
import LanguagePicker from '@/components/LanguagePicker.vue';
import ScanToScreen from '@/components/ScanToScreen.vue';

/**
 * The shell.  design.md §13.2
 *
 * "Four surfaces, one design system": Catalog, Pricing, Warehouse, Admin.
 * Catalog and the P2 selling screens exist; Warehouse does not, and there is no
 * menu item for it — §1.3's rule that a menu item for a feature that is not
 * there reads as a feature that exists applies to navigation more literally
 * than to anything else.
 */
const route = useRoute();

/**
 * The navigation drawer, on a phone.
 *
 * Closed by navigating, because a drawer left open over the screen you just
 * asked for is the most common way this pattern annoys people.
 */
const navOpen = ref(false);
watch(
  () => route.fullPath,
  () => (navOpen.value = false),
);
</script>

<template>
  <!--
    Two kinds of screen get no shell.

    PUBLIC — sign-in and the OAuth callback. Showing the app shell around them
    implies a session that does not exist yet.

    BARE — documents. A pick list is a piece of paper that happens to be
    rendered in a browser, and the sidebar is not part of it: on screen it
    steals a fifth of the width from a table an operator reads at arm's length,
    and on paper it would print. Hiding it with @media print instead would mean
    a scoped rule in a child component reaching up to style the shell, which
    only works until somebody adds a second document.

    Bare is NOT public: the guard in router.ts keys on , so these
    still require a session.
  -->
  <!-- Everywhere, including the bare document screens: somebody holding a
       scanner at a pick list wants the next scan to take them somewhere, and a
       shell they cannot see is no reason for it not to. Not on the public ones,
       which have no session to look anything up with. -->
  <ScanToScreen v-if="!route.meta.public" />

  <RouterView v-if="route.meta.public || route.meta.bare" />

  <div v-else class="shell">
    <!-- Phone only. The journeys document is blunt about this: "everything
         must work on a phone, because stock gets counted standing at a shelf" —
         and a permanent 200px sidebar on a 390px screen leaves 190. -->
    <button class="menu-button" :aria-expanded="navOpen" @click="navOpen = !navOpen">
      <span aria-hidden="true">☰</span>
      <span class="sr-only">{{ navOpen ? 'Close menu' : 'Open menu' }}</span>
      <span class="text-[15px] font-extrabold tracking-[-0.045em]">lasti</span>
    </button>

    <!-- Tapping anywhere else closes it, which is what everybody tries first. -->
    <button v-if="navOpen" class="scrim" aria-label="Close menu" @click="navOpen = false"></button>

    <aside class="side flex flex-col border-r border-line bg-paper" :class="{ 'is-open': navOpen }">
      <div class="flex h-14 items-center gap-2 border-b border-line px-4">
        <svg viewBox="0 0 24 24" class="size-5 shrink-0" aria-hidden="true">
          <rect
            x="3.75"
            y="2.75"
            width="16.5"
            height="18.5"
            rx="3.25"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <path
            d="M4.75 13h14.5v5.25a2.25 2.25 0 0 1-2.25 2.25H7a2.25 2.25 0 0 1-2.25-2.25V13Z"
            fill="var(--color-amber)"
          />
          <path d="M4.75 13h14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
        <span class="text-[17px] font-extrabold tracking-[-0.045em] text-ink">lasti</span>
      </div>

      <nav class="flex-1 p-2" aria-label="Sections">
        <RouterLink to="/catalog" class="nav-link">Catalog</RouterLink>
        <RouterLink to="/categories" class="nav-link">Categories</RouterLink>
        <RouterLink to="/properties" class="nav-link">Properties</RouterLink>

        <!-- Selling, as opposed to what is for sale. Separated by a rule rather
             than by a heading: two groups of three do not need labelling, and a
             sidebar that explains itself is a sidebar with too much in it. -->
        <RouterLink to="/orders" class="nav-link mt-2 border-t border-line pt-3">
          Orders
        </RouterLink>
        <RouterLink to="/customers" class="nav-link">Customers</RouterLink>
        <RouterLink to="/invoices" class="nav-link">Invoices</RouterLink>
        <RouterLink to="/settlements" class="nav-link">Settlements</RouterLink>
        <RouterLink to="/reviews" class="nav-link">Reviews</RouterLink>
        <RouterLink to="/campaigns" class="nav-link">Campaigns</RouterLink>
        <RouterLink to="/availability" class="nav-link">Availability</RouterLink>
        <RouterLink to="/warehouse" class="nav-link">Warehouse</RouterLink>
        <RouterLink to="/settings" class="nav-link mt-2 border-t border-line pt-3">
          Settings
        </RouterLink>
      </nav>

      <!-- One choice for the whole back office, so it belongs to the chrome and
           not to a screen. §17.1 -->
      <div class="border-t border-line p-3">
        <div class="mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-ink-muted">
          Language
        </div>
        <LanguagePicker />
      </div>

      <div class="border-t border-line p-3">
        <div class="text-[10px] font-bold uppercase tracking-[0.12em] text-ink-muted">
          Signed in
        </div>
        <div class="mt-1 truncate text-ink" :title="subject ?? undefined">
          {{ displayName ?? 'unknown' }}
        </div>

        <div
          v-if="usingDevAuth"
          class="mt-2 rounded-[4px] bg-amber-wash px-2 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-amber-ink"
          title="Development only — not real authentication"
        >
          Dev auth
        </div>
        <button v-else class="btn mt-2 h-7 w-full text-[12px]" @click="signOut">Sign out</button>
      </div>
    </aside>

    <main class="min-w-0">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
/*
 * Two layouts, one shell.
 *
 * Wide: a permanent 200px column, as before. Narrow: one column, a menu button
 * in the top-left, and the nav slides over the content — which is the only
 * arrangement that leaves a phone enough room to show a product.
 */
.shell {
  display: grid;
  min-height: 100vh;
  grid-template-columns: 200px 1fr;
}

.menu-button {
  display: none;
}

@media (max-width: 767px) {
  .shell {
    grid-template-columns: 1fr;
  }

  .menu-button {
    position: sticky;
    top: 0;
    z-index: 30;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 44px;
    padding-inline: 12px;
    border: 0;
    border-bottom: 1px solid var(--color-line);
    background: var(--color-paper);
    color: var(--color-ink);
    font-size: 17px;
    cursor: pointer;
  }

  .side {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 40;
    width: 240px;
    /* Off-screen rather than hidden, so it can slide and so the links stay
       reachable to a screen reader in the order they are read. */
    transform: translateX(-100%);
    transition: transform 0.18s ease;
  }
  .side.is-open {
    transform: none;
  }

  .scrim {
    position: fixed;
    inset: 0;
    z-index: 35;
    border: 0;
    background: rgb(0 0 0 / 0.3);
  }
}

@media (prefers-reduced-motion: reduce) {
  .side {
    transition: none;
  }
}

.nav-link {
  display: block;
  padding: 7px 10px;
  border-radius: var(--radius-ui);
  color: var(--color-ink-body);
  font-weight: 600;
  text-decoration: none;
}
.nav-link:hover {
  background: var(--color-line-soft);
  color: var(--color-ink);
}
.nav-link.router-link-active {
  background: var(--color-surface);
  color: var(--color-ink);
  box-shadow: inset 2px 0 0 var(--color-amber);
}
</style>
